import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Slider } from './TheaterPanel';

export interface ViewSpeeds {
  rotate3d: number;
  pan3d: number;
  zoom3d: number;
  rotate2d: number;
  pan2d: number;
  zoom2d: number;
}

/** CONTROLS GUIDE (ControlsHelp.cs), the rows that apply to the read-only viewer. */
const GUIDE: { title: string; rows: [string, string][] }[] = [
  {
    title: '3D VIEW — CAMERA',
    rows: [
      ['LMB Drag', 'Orbit around the volume.'],
      ['RMB Drag', 'Pan the view.'],
      ['Scroll', 'Zoom in / out.'],
      ['MMB Drag', 'Zoom — drag up or down.'],
      ['LMB + RMB Drag', 'Window / level — up-down changes the level, left-right changes the width.'],
      ['MMB + RMB Drag', 'Camera clip — drag up to cut deeper into the volume, down to pull back.'],
      ['F', 'Focus the camera on the volume.'],
      ['A P L R S I', 'Snap to the Anterior, Posterior, Left, Right, Superior or Inferior view — or click a compass letter.'],
      ['LMB Drag ring', 'Roll the camera in the screen plane.'],
    ],
  },
  {
    title: '3D VIEW — POINTS',
    rows: [
      ['Pencil', 'The pencil on a nadir, commissure, ostium or coaptation row turns edit mode on; the point grows a halo.'],
      ['Double-click', 'Double-click a point to edit it; double-click empty space to leave every edit mode.'],
      ['LMB Drag point', 'Move an editable point. It snaps to the segmentation surfaces and the first visible voxel (Snap menu in the toolbar).'],
      ['Ctrl + Drag', 'Move freely, without snapping.'],
      ['Ctrl + Scroll', 'While dragging: push the point away or pull it closer along the view ray.'],
      ['Nadirs', 'Moving a nadir refits the VBR ring and plane, the Root Centroid, the heights and the diameters on release.'],
      ['Commissures', 'Moving a commissure warps the ends of the free margins, and updates the heights, lengths and angles.'],
      ['Coaptation', 'The coaptation point slides along the Root Centroid.'],
      ['Reset', 'The reset arrow on an edited row restores the loaded version\'s position (or the AI).'],
      ['Saves', 'Original (AI) plus the saved versions. Save overwrites the active version, Save as… creates one, Delete removes it. Switching versions drops unsaved edits.'],
    ],
  },
  {
    title: 'SLICE VIEWS',
    rows: [
      ['LMB Drag dot', 'Move a point in edit mode on the slice plane (its depth stays). Double-click a dot to edit it.'],
      ['Scroll', 'Step through slices.'],
      ['LMB Drag', 'Scrub through slices (start on empty area).'],
      ['RMB Drag', 'Pan the slice.'],
      ['MMB Drag', 'Zoom this pane.'],
      ['MMB Click', 'Jump the crosshair to the clicked point.'],
      ['LMB + RMB Drag', 'Window / level — up-down changes the level, left-right changes the width.'],
      ['LMB Drag center', 'Move both slice planes at once.'],
      ['LMB Drag arm', 'Near the center: move that slice. Toward the edge: rotate it.'],
      ['Buttons', 'Expand opens the pane fullscreen (the X button exits), the grid button toggles the 2 × 2 quad view, the eye shows / hides the slice plane and the slider sets the slice position. The header arrow opens zoom and window / level settings.'],
    ],
  },
  {
    title: 'FRAMES & PLAYBACK',
    rows: [
      ['Prev / Next', 'Step one frame back / forward (toolbar chevrons or the Theater tab). Multi-frame studies only.'],
      ['Play', 'Start / stop cine playback; the speed slider sets the playback rate.'],
    ],
  },
  {
    title: 'PANEL TOOLS',
    rows: [
      ['Tabs', 'Heart = Inventory (measurements), box = Theater (volume rendering & presets).'],
      ['Toolbar', 'Quick bar: labels, hide-all inventory override (hides every inventory item and label on every frame without changing the rows\' own eye state), volumetric view, orthographic camera, Clean Root / Endocast preset and frame stepping.'],
      ['Rows', 'The eye shows / hides an item, the star isolates it and the swatch recolors it.'],
      ['Gear', 'View settings — adjust the rotate, pan and zoom speeds of the 3D and slice views.'],
    ],
  },
];

export function ControlsGuideDialog({ onClose }: { onClose: () => void }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal wide" onClick={(e) => e.stopPropagation()}>
        <h2>Controls guide</h2>
        {GUIDE.map((g) => (
          <div key={g.title}>
            <h3>{g.title}</h3>
            <table className="kv guide">
              <tbody>
                {g.rows.map(([k, v]) => (
                  <tr key={k + v}>
                    <td className="mono">{k}</td>
                    <td>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
        <button onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

export function ViewSettingsDialog({ speeds, onChange, onReset, onClose }: { speeds: ViewSpeeds; onChange: (s: ViewSpeeds) => void; onReset: () => void; onClose: () => void }) {
  const row = (key: keyof ViewSpeeds, label: string) => (
    <Slider key={key} label={`${label} ${speeds[key].toFixed(2)}`} value={speeds[key]} min={0.1} max={3} step={0.05} onChange={(v) => onChange({ ...speeds, [key]: v })} />
  );
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>View settings</h2>
        <h3>3D VIEW</h3>
        {row('rotate3d', 'Rotate speed')}
        {row('pan3d', 'Pan speed')}
        {row('zoom3d', 'Zoom speed')}
        <h3>SLICE VIEWS</h3>
        {row('rotate2d', 'Rotate speed')}
        {row('pan2d', 'Pan speed')}
        {row('zoom2d', 'Zoom speed')}
        <p className="muted small">Speeds are multipliers on the default (1). Settings apply to all projects.</p>
        <div className="row">
          <button className="secondary" onClick={onReset}>
            Reset to defaults
          </button>
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/** NamePopup: a one-line name prompt (Save As). `onSubmit` returns false to keep it open. */
export function NamePromptDialog({ title, label, initial, onSubmit, onClose }: { title: string; label: string; initial?: string; onSubmit: (name: string) => Promise<boolean> | boolean; onClose: () => void }) {
  const [name, setName] = useState(initial ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Enter a name.');
      return;
    }
    setBusy(true);
    const ok = await onSubmit(trimmed);
    setBusy(false);
    if (!ok) setError('The name needs at least one letter or digit.');
  };
  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{title}</h2>
        <label>
          {label}
          <input
            type="text"
            autoFocus
            maxLength={120}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit();
              if (e.key === 'Escape') onClose();
            }}
          />
        </label>
        {error && <div className="error small">{error}</div>}
        <div className="row right">
          <button className="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button onClick={() => void submit()} disabled={busy}>
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmDialog({ title, message, confirmLabel, onConfirm, onClose }: { title: string; message: string; confirmLabel: string; onConfirm: () => Promise<void> | void; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{title}</h2>
        <p>{message}</p>
        <div className="row right">
          <button className="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="danger"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              await onConfirm();
              setBusy(false);
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
